import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
   getDunning,
   listDunningEvents,
} from '../../../../services/finance/dunningApi';
import { financeErrorMessage } from '../../utils/apiError';
import { EVENTS_PER_PAGE } from '../constants';

// ---------------------------------------------------------------------------
// Detalhe do caso aberto no drawer: card completo (com `interactions`) +
// eventos de auditoria, em paralelo. A timeline visual é o merge dos dois
// (montado no componente via buildTimeline).
//
// `setCaseData` existe porque toda mutação responde o caso inteiro — o drawer
// aplica a resposta direto, sem refetch. Os EVENTOS, porém, só nascem no
// backend, então mutações pedem `refetchEvents` junto.
// ---------------------------------------------------------------------------

export default function useDunningCase(caseId) {
   const [caseData, setCaseData] = useState(null);
   const [events, setEvents] = useState([]);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState(null);

   const seqRef = useRef(0);

   const fetchCase = useCallback(
      async ({ quiet = false } = {}) => {
         if (!caseId) return;
         seqRef.current += 1;
         const seq = seqRef.current;

         if (!quiet) {
            setLoading(true);
            setError(null);
         }

         try {
            const [detail, eventsResult] = await Promise.all([
               getDunning(caseId),
               listDunningEvents(caseId, { perPage: EVENTS_PER_PAGE }),
            ]);
            if (seqRef.current !== seq) return;
            setCaseData(detail);
            setEvents(eventsResult.events);
            setLoading(false);
            setError(null);
         } catch (err) {
            if (seqRef.current !== seq) return;
            setLoading(false);
            if (!quiet) {
               setError(err);
               toast.error(
                  financeErrorMessage(err, 'Falha ao carregar o caso.')
               );
            }
         }
      },
      [caseId]
   );

   useEffect(() => {
      // Troca de caso: zera o anterior para o drawer abrir em skeleton, não
      // com o conteúdo do caso anterior.
      setCaseData(null);
      setEvents([]);
      setError(null);
      if (caseId) fetchCase();
   }, [caseId, fetchCase]);

   const refetchEvents = useCallback(async () => {
      if (!caseId) return;
      try {
         const result = await listDunningEvents(caseId, {
            perPage: EVENTS_PER_PAGE,
         });
         setEvents(result.events);
      } catch {
         // Silencioso de propósito: o refresh de auditoria é complemento de uma
         // mutação que JÁ deu certo e já foi confirmada ao usuário.
      }
   }, [caseId]);

   return {
      caseData,
      events,
      loading,
      error,
      refetch: fetchCase,
      refetchEvents,
      setCaseData,
   };
}
